import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { AppLayout, PageHeader } from "@/components/Layout";
import { fileUrl } from "@/components/ImageUpload";
import { toast } from "sonner";
import { ShoppingBag, ShoppingCart, X, Plus, Minus, Loader2, CheckCircle2, Trash2, ArrowLeft, Lock } from "lucide-react";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const CART_KEY = "bmm_shop_cart";

const loadCart = () => {
  try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; }
};

export default function Shop() {
  const [items, setItems] = useState(null);
  const [ship, setShip] = useState({ base_cents: 1000, per_extra_cents: 200, free_threshold: 3 });
  const [cart, setCart] = useState(loadCart);
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(null);   // product being configured
  const [receipt, setReceipt] = useState(null);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  const load = useCallback(() => {
    api.get("/shop/items").then((r) => { setItems(r.data.items); if (r.data.shipping) setShip(r.data.shipping); })
      .catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Return-from-Stripe handling
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sess = p.get("shop_session");
    const cancel = p.get("shop_cancel");
    if (cancel) {
      toast("Checkout cancelled — you were not charged.");
      window.history.replaceState({}, "", "/shop");
      return;
    }
    if (!sess) return;
    let tries = 0, stop = false;
    setReceipt({ loading: true });
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/shop/order/${sess}`);
        if (data.payment_status === "paid") {
          setReceipt({ loading: false, order: data });
          setCart([]);
          window.history.replaceState({}, "", "/shop");
          return;
        }
      } catch (e) { /* keep polling */ }
      if (!stop && tries < 12) setTimeout(poll, 2000);
      else if (!stop) { setReceipt({ loading: false, pending: true }); window.history.replaceState({}, "", "/shop"); }
    };
    poll();
    return () => { stop = true; };
  }, []);

  const qtyTotal = useMemo(() => cart.reduce((n, l) => n + l.quantity, 0), [cart]);
  const subtotal = useMemo(() => cart.reduce((n, l) => n + l.unit_price_cents * l.quantity, 0), [cart]);
  const shipCents = useMemo(() => {
    if (!qtyTotal) return 0;
    return ship.base_cents + Math.max(0, qtyTotal - ship.free_threshold) * ship.per_extra_cents;
  }, [qtyTotal, ship]);

  const addToCart = (product, size, color, quantity) => {
    const key = `${product.id}|${size || ""}|${color || ""}`;
    setCart((c) => {
      const i = c.findIndex((l) => l.key === key);
      if (i >= 0) { const next = [...c]; next[i] = { ...next[i], quantity: next[i].quantity + quantity }; return next; }
      return [...c, { key, item_id: product.id, name: product.name, size: size || null, color: color || null,
        quantity, unit_price_cents: product.price_cents, image: (product.images || [])[0] }];
    });
    setPicking(null);
    setOpen(true);
    toast.success(`${product.name} added to cart`);
  };

  const clickAdd = (product) => {
    const needsChoice = (product.sizes?.length || 0) > 0 || (product.colors?.length || 0) > 0;
    if (needsChoice) setPicking(product);
    else addToCart(product, null, null, 1);
  };

  const setQty = (key, delta) => setCart((c) => c.map((l) => l.key === key ? { ...l, quantity: Math.max(1, l.quantity + delta) } : l));
  const removeLine = (key) => setCart((c) => c.filter((l) => l.key !== key));

  if (receipt) return <Receipt receipt={receipt} onClose={() => setReceipt(null)} />;

  return (
    <AppLayout>
      <PageHeader subtitle="Official Merch" title="Shop"
        action={
          <button data-testid="shop-open-cart" onClick={() => setOpen(true)}
            className="relative grid h-11 w-11 place-items-center rounded-full bg-primary text-primary-foreground active:scale-95">
            <ShoppingCart size={20} />
            {qtyTotal > 0 && <span data-testid="shop-cart-count" className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-secondary px-1 text-[11px] font-bold text-white">{qtyTotal}</span>}
          </button>
        } />

      {items === null ? (
        <div className="grid place-items-center py-24 text-muted-foreground"><Loader2 className="animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="mx-4 mt-6 rounded-xl border border-dashed border-border bg-card p-10 text-center">
          <ShoppingBag className="mx-auto mb-3 text-muted-foreground" size={32} />
          <p className="font-semibold text-primary">Merch is coming soon</p>
          <p className="mt-1 text-sm text-muted-foreground">Check back shortly for official Bluegrass Mustang Makeover gear.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 px-4 pt-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`shop-item-${it.id}`} className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm">
              <div className="aspect-square w-full bg-muted">
                {(it.images || [])[0]
                  ? <img src={fileUrl(it.images[0])} alt={it.name} className="h-full w-full object-cover" />
                  : <div className="grid h-full place-items-center text-muted-foreground"><ShoppingBag size={28} /></div>}
              </div>
              <div className="flex flex-1 flex-col p-3">
                {it.category && <p className="text-[10px] font-bold uppercase tracking-wider text-secondary">{it.category}</p>}
                <p className="font-semibold leading-tight text-primary">{it.name}</p>
                {it.description && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{it.description}</p>}
                <div className="mt-auto flex items-center justify-between pt-3">
                  <span className="font-display text-lg font-bold text-primary">{money(it.price_cents)}</span>
                  <button data-testid={`shop-add-${it.id}`} onClick={() => clickAdd(it)}
                    className="flex h-9 items-center gap-1 rounded-full bg-primary px-3 text-sm font-semibold text-primary-foreground active:scale-95">
                    <Plus size={15} /> Add
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {picking && <ProductPicker product={picking} onCancel={() => setPicking(null)} onAdd={addToCart} />}
      {open && (
        <CartDrawer cart={cart} subtotal={subtotal} shipCents={shipCents} qtyTotal={qtyTotal} ship={ship}
          onClose={() => setOpen(false)} setQty={setQty} removeLine={removeLine} />
      )}
    </AppLayout>
  );
}

function ProductPicker({ product, onCancel, onAdd }) {
  const [size, setSize] = useState(product.sizes?.[0] || "");
  const [color, setColor] = useState(product.colors?.[0] || "");
  const [qty, setQty] = useState(1);
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 sm:items-center" onClick={onCancel}>
      <div data-testid="shop-product-picker" className="w-full max-w-lg rounded-t-2xl bg-card p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-display text-xl font-bold text-primary">{product.name}</p>
            <p className="font-semibold text-secondary">{money(product.price_cents)}</p>
          </div>
          <button data-testid="shop-picker-close" onClick={onCancel} className="grid h-9 w-9 place-items-center rounded-full bg-muted text-muted-foreground"><X size={18} /></button>
        </div>
        {(product.images || [])[0] && <img src={fileUrl(product.images[0])} alt="" className="mt-3 h-44 w-full rounded-lg object-cover" />}
        {product.colors?.length > 0 && (
          <div className="mt-4">
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Color</label>
            <select data-testid="shop-picker-color" value={color} onChange={(e) => setColor(e.target.value)}
              className="mt-1 h-11 w-full rounded-lg border border-border bg-background px-3 text-sm">
              {product.colors.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
        {product.sizes?.length > 0 && (
          <div className="mt-3">
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Size</label>
            <select data-testid="shop-picker-size" value={size} onChange={(e) => setSize(e.target.value)}
              className="mt-1 h-11 w-full rounded-lg border border-border bg-background px-3 text-sm">
              {product.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div className="mt-3 flex items-center gap-3">
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Qty</label>
          <div className="flex items-center gap-2">
            <button data-testid="shop-picker-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="grid h-9 w-9 place-items-center rounded-full border border-border"><Minus size={15} /></button>
            <span className="w-6 text-center font-semibold">{qty}</span>
            <button data-testid="shop-picker-plus" onClick={() => setQty((q) => q + 1)} className="grid h-9 w-9 place-items-center rounded-full border border-border"><Plus size={15} /></button>
          </div>
        </div>
        <button data-testid="shop-picker-add" onClick={() => onAdd(product, size, color, qty)}
          className="mt-5 h-12 w-full rounded-full bg-primary font-semibold text-primary-foreground active:scale-95">
          Add to Cart · {money(product.price_cents * qty)}
        </button>
      </div>
    </div>
  );
}

function CartDrawer({ cart, subtotal, shipCents, qtyTotal, ship, onClose, setQty, removeLine }) {
  const [stage, setStage] = useState("cart");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", line1: "", line2: "", city: "", state: "KY", postal_code: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const total = subtotal + shipCents;

  const pay = async () => {
    for (const [k, label] of [["name", "full name"], ["email", "email"], ["line1", "street address"], ["city", "city"], ["state", "state"], ["postal_code", "ZIP code"]]) {
      if (!form[k]?.trim()) return toast.error(`Please enter your ${label}.`);
    }
    setBusy(true);
    try {
      const { data } = await api.post("/shop/checkout", {
        items: cart.map((l) => ({ item_id: l.item_id, size: l.size, color: l.color, quantity: l.quantity })),
        email: form.email.trim(), phone: form.phone.trim(),
        shipping: { name: form.name.trim(), line1: form.line1.trim(), line2: form.line2.trim(),
          city: form.city.trim(), state: form.state, postal_code: form.postal_code.trim(), country: "US" },
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/50" onClick={onClose}>
      <div data-testid="shop-cart-drawer" className="flex h-full w-full max-w-md flex-col bg-background" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            {stage === "address" && <button data-testid="shop-back-to-cart" onClick={() => setStage("cart")} className="grid h-9 w-9 place-items-center rounded-full bg-muted"><ArrowLeft size={18} /></button>}
            <p className="font-display text-lg font-bold text-primary">{stage === "cart" ? "Your Cart" : "Shipping Details"}</p>
          </div>
          <button data-testid="shop-cart-close" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full bg-muted text-muted-foreground"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {cart.length === 0 ? (
            <div className="grid h-full place-items-center text-center text-muted-foreground">
              <div><ShoppingCart className="mx-auto mb-2" size={28} /><p>Your cart is empty.</p></div>
            </div>
          ) : stage === "cart" ? (
            <div className="space-y-3">
              {cart.map((l) => (
                <div key={l.key} data-testid={`cart-line-${l.item_id}`} className="flex gap-3 rounded-xl border border-border bg-card p-3">
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-muted">
                    {l.image ? <img src={fileUrl(l.image)} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-muted-foreground"><ShoppingBag size={18} /></div>}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold leading-tight text-primary">{l.name}</p>
                      <button data-testid={`cart-remove-${l.item_id}`} onClick={() => removeLine(l.key)} className="text-destructive"><Trash2 size={16} /></button>
                    </div>
                    {(l.color || l.size) && <p className="text-xs text-muted-foreground">{[l.color, l.size].filter(Boolean).join(" · ")}</p>}
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button data-testid={`cart-minus-${l.item_id}`} onClick={() => setQty(l.key, -1)} className="grid h-7 w-7 place-items-center rounded-full border border-border"><Minus size={13} /></button>
                        <span className="w-5 text-center text-sm font-semibold">{l.quantity}</span>
                        <button data-testid={`cart-plus-${l.item_id}`} onClick={() => setQty(l.key, 1)} className="grid h-7 w-7 place-items-center rounded-full border border-border"><Plus size={13} /></button>
                      </div>
                      <span className="font-semibold text-primary">{money(l.unit_price_cents * l.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <Field label="Full Name" testid="ship-name" value={form.name} onChange={set("name")} />
              <Field label="Email" testid="ship-email" type="email" value={form.email} onChange={set("email")} />
              <Field label="Phone (optional)" testid="ship-phone" value={form.phone} onChange={set("phone")} />
              <Field label="Street Address" testid="ship-line1" value={form.line1} onChange={set("line1")} />
              <Field label="Apt / Suite (optional)" testid="ship-line2" value={form.line2} onChange={set("line2")} />
              <Field label="City" testid="ship-city" value={form.city} onChange={set("city")} />
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">State</label>
                  <select data-testid="ship-state" value={form.state} onChange={set("state")}
                    className="mt-1 h-11 w-full rounded-lg border border-border bg-background px-3 text-sm">
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="flex-1"><Field label="ZIP Code" testid="ship-zip" value={form.postal_code} onChange={set("postal_code")} /></div>
              </div>
              <p className="rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground">We ship within the United States only. Flat {money(ship.base_cents)} shipping for up to {ship.free_threshold} items, +{money(ship.per_extra_cents)} per item after that.</p>
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-border bg-card p-4">
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold">{money(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Shipping ({qtyTotal} item{qtyTotal !== 1 ? "s" : ""})</span><span className="font-semibold">{money(shipCents)}</span></div>
              <div className="flex justify-between pt-1 text-base"><span className="font-bold text-primary">Total</span><span className="font-display font-bold text-primary">{money(total)}</span></div>
            </div>
            {stage === "cart" ? (
              <button data-testid="shop-checkout-btn" onClick={() => setStage("address")}
                className="mt-3 h-12 w-full rounded-full bg-primary font-semibold text-primary-foreground active:scale-95">Checkout</button>
            ) : (
              <button data-testid="shop-pay-btn" onClick={pay} disabled={busy}
                className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-secondary font-semibold text-white active:scale-95 disabled:opacity-60">
                {busy ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />} Pay {money(total)}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, testid, value, onChange, type = "text" }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</label>
    <input data-testid={testid} type={type} value={value} onChange={onChange}
      className="mt-1 h-11 w-full rounded-lg border border-border bg-background px-3 text-sm" />
  </div>
);

function Receipt({ receipt, onClose }) {
  return (
    <AppLayout>
      <div className="grid min-h-[70vh] place-items-center px-4">
        <div data-testid="shop-receipt" className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
          {receipt.loading ? (
            <><Loader2 className="mx-auto animate-spin text-secondary" size={32} /><p className="mt-3 font-semibold text-primary">Confirming your payment…</p></>
          ) : receipt.order ? (
            <>
              <CheckCircle2 className="mx-auto text-green-600" size={40} />
              <p className="mt-3 font-display text-xl font-bold text-primary">Thank you for your order!</p>
              <p className="mt-1 text-sm text-muted-foreground">A confirmation was sent to {receipt.order.email}. We'll ship to {receipt.order.shipping?.city}, {receipt.order.shipping?.state}.</p>
              <div className="mt-4 rounded-xl bg-muted/60 p-3 text-left text-sm">
                {(receipt.order.items || []).map((l, i) => (
                  <div key={i} className="flex justify-between py-0.5">
                    <span>{l.quantity}× {l.label || l.name}</span>
                    <span className="font-semibold">{money(l.line_total_cents)}</span>
                  </div>
                ))}
                <div className="mt-2 flex justify-between border-t border-border pt-2"><span>Shipping</span><span className="font-semibold">{money(receipt.order.shipping_cents)}</span></div>
                <div className="flex justify-between"><span className="font-bold text-primary">Total</span><span className="font-display font-bold text-primary">{money(receipt.order.amount_cents)}</span></div>
              </div>
            </>
          ) : (
            <><p className="font-display text-lg font-bold text-primary">Payment pending</p><p className="mt-1 text-sm text-muted-foreground">If you completed payment, your order will appear shortly. Contact us if you have questions.</p></>
          )}
          <button data-testid="shop-receipt-close" onClick={onClose} className="mt-5 h-11 w-full rounded-full bg-primary font-semibold text-primary-foreground">Back to Shop</button>
        </div>
      </div>
    </AppLayout>
  );
}
